// Helpers to keep user-typed whitespace from being lost when draft-js editor
// content is converted to HTML and later rendered.
//
// Two things get lost by default:
//  1. Runs of consecutive spaces collapse to a single space on render.
//  2. Blank lines (empty draft-js blocks) become empty <p></p> that the
//     message editors strip out.

/**
 * Keeps consecutive spaces from collapsing on render by converting the extra
 * ones to non-breaking spaces. Operates only on text nodes so HTML tags and
 * attributes (e.g. mention spans, links) are left untouched.
 *
 * @param {string} html HTML produced by draft-convert's convertToHTML
 * @returns {string}
 */
export const preserveExtraSpaces = (html) => {
  if (!html || typeof document === "undefined") return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    // Keep the first space, replace the rest of the run with U+00A0 so the
    // spacing survives HTML whitespace collapsing when the message renders.
    node.nodeValue = node.nodeValue.replace(
      / {2,}/g,
      (match) => " " + "\u00a0".repeat(match.length - 1),
    );
  });
  return doc.body.innerHTML;
};

/**
 * Preserves intentional blank lines: draft-js emits them as empty paragraphs,
 * so keep them as <p><br></p> instead of stripping them out.
 *
 * @param {string} html
 * @returns {string}
 */
export const preserveBlankLines = (html) =>
  html.replace(/<p([^>]*)>(\s|&nbsp;)*<\/p>/g, "<p$1><br></p>");
